import type React from "react";

import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "~/atoms/card";
import { RichTextEditor } from "~/molecules/RichTextEditor";
import { StepHeader } from "~/molecules/WorkflowBuilderStepHeader";
import { StepUsers } from "~/molecules/WorkflowBuilderStepUsers";
import {
  type WorkflowStepInput,
  type WorkflowStepUser,
  type WorkflowStepType,
  type WorkflowStepUserPermission,
  WorkflowStepStatuses,
} from "~/api/types";
import { cn } from "~/lib/utils";
import { Label } from "~/atoms/label";
import { useSortable } from "@dnd-kit/react/sortable";
import DatePicker from "./DatePicker";
import { ControlledCombobox } from "./ControlledCombobox";
import { canEditWorkflowStep } from "~/helpers/documentPermissions";
import { type StepDateIssue } from "~/helpers/workflowDates";

type WorkflowBuilderStepProps = {
  step: WorkflowStepInput;
  index: number;
  onRemove: (stepKey: string) => void;
  onUpdate: (stepKey: string, step: WorkflowStepInput) => void;
  isEditMode: boolean;
  categoryId?: number;
  dueWarning?: StepDateIssue;
  minDueAt?: string;
  maxDueAt?: string;
  hideDueDates?: boolean;
  hasStepInstance: boolean;
};

// memo keeps a keystroke in one step's rich text editor from re-rendering
// every sibling step's editor
export const WorkflowBuilderStep = memo(function WorkflowBuilderStep({
  step,
  index,
  onRemove,
  onUpdate,
  isEditMode,
  categoryId,
  dueWarning,
  minDueAt,
  maxDueAt,
  hideDueDates,
  hasStepInstance,
}: WorkflowBuilderStepProps) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<{ title?: string }>({});

  // A step is editable if it hasn't been reached yet (upcoming status or no id means new)
  const isUpcoming = step.status === WorkflowStepStatuses.upcoming;
  const canBeEdited = isEditMode && canEditWorkflowStep(step);
  const canDragStep = isEditMode && isUpcoming;
  const { ref, isDragging } = useSortable({
    id: step._key,
    index,
    type: "WORKFLOW_STEP",
    accept: "WORKFLOW_STEP",
    disabled: !canDragStep,
  });

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;

    if (!newTitle) {
      setErrors({ title: t(($) => $.commons.workflow.stepTitleRequired) });
    } else {
      setErrors({});
    }

    onUpdate(step._key, {
      ...step,
      name: newTitle,
    });
  };

  const handleDescriptionChange = (html: string) => {
    onUpdate(step._key, {
      ...step,
      description: html,
    });
  };

  const handleAddUser = (newUser: WorkflowStepUser) => {
    onUpdate(step._key, {
      ...step,
      users: [...step.users, newUser],
    });
  };

  const handleRemoveUser = (userId: number) => {
    onUpdate(step._key, {
      ...step,
      users: step.users.filter((user) => user.userId !== userId),
    });
  };

  const handleEditUserPermission = (
    userId: number,
    permission: WorkflowStepUserPermission,
  ) => {
    onUpdate(step._key, {
      ...step,
      users: step.users.map((user) =>
        user.userId === userId ? { ...user, permission } : user,
      ),
    });
  };

  const handleTypeChange = (type: string) => {
    onUpdate(step._key, {
      ...step,
      type: type as WorkflowStepType,
    });
  };

  const handleDueDateChange = (dueDate?: Date) => {
    onUpdate(step._key, {
      ...step,
      dueAt: dueDate ?? null,
    });
  };

  return (
    <Card
      ref={ref}
      className={cn(
        "w-full overflow-hidden border pt-0 transition-all duration-200",
        {
          "bg-info/4": step.status === WorkflowStepStatuses.active,
          "bg-success/4": step.status === WorkflowStepStatuses.completed,
          "border-primary/50 bg-primary/5 scale-[0.97] border-2 border-dashed opacity-50":
            isDragging,
          "hover:shadow-md": isEditMode && isUpcoming,
        },
      )}
    >
      <StepHeader
        index={index}
        isEditMode={isEditMode}
        step={step}
        onTitleChange={handleTitleChange}
        onRemove={() => onRemove(step._key)}
        error={errors.title}
        hasStepInstance={hasStepInstance}
      />
      <CardContent className="flex flex-col gap-2 pt-4">
        <div
          className={cn(
            "grid gap-3",
            hideDueDates ? "grid-cols-1" : "grid-cols-2",
          )}
        >
          <div>
            <Label>{t(($) => $.commons.workflow.type)}</Label>
            <ControlledCombobox
              value={step.type}
              onChange={handleTypeChange}
              disabled={!canBeEdited}
              options={[
                {
                  value: "review",
                  label: t(($) => $.commons.workflow.review),
                },
                {
                  value: "approval",
                  label: t(($) => $.commons.workflow.approval),
                },
              ]}
            />
          </div>
          {!hideDueDates && (
            <div>
              <Label>{t(($) => $.commons.workflow.responseDate)}</Label>
              <DatePicker
                value={step.dueAt}
                minDate={minDueAt ? new Date(minDueAt) : undefined}
                maxDate={maxDueAt ? new Date(maxDueAt) : undefined}
                disabled={!canBeEdited}
                onChange={handleDueDateChange}
              />
              {dueWarning && (
                <p className="text-destructive text-xs">
                  {dueWarning === "afterReviewTo"
                    ? t(
                        ($) =>
                          $.commons.workflow.responseDateAfterReviewToWarning,
                      )
                    : t(($) => $.commons.workflow.responseDateWarning)}
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <Label>{t(($) => $.commons.workflow.description)}</Label>
          <RichTextEditor
            value={step.description ?? ""}
            onChange={handleDescriptionChange}
            disabled={!canBeEdited}
            placeholder={t(($) => $.commons.workflow.stepDescription)}
          />
        </div>

        <div>
          <Label>{t(($) => $.commons.workflow.assignedUsers)}</Label>
          <StepUsers
            users={step.users}
            canBeEdited={canBeEdited}
            onRemoveUser={handleRemoveUser}
            onEditUserPermission={handleEditUserPermission}
            onAddUser={handleAddUser}
            categoryId={categoryId}
          />
        </div>
      </CardContent>
    </Card>
  );
});
